import React, { Children, useRef, useState } from 'react';
import ShowMore from './ShowMore';
import useGridColumns from './useGridColumns';
import './CardGrid.css';

/**
 * Baustein 5 — Card-Grid.
 * Responsives Grid für `<CardTile>`-Kacheln.
 *
 * Varianten:
 *   variant='cards'    — Sammlung-typisch (minmax 320px, dense gap)
 *   variant='variants' — Print-Picker / Variant-View (minmax 220px, normal gap)
 *   variant='browse'   — Stöber-Raster für lange Trefferlisten in schmalen Flächen
 *                        (Kartenbild ≥ 260px, ≤ 320px, dense gap): das Kartenbild trägt
 *                        die Information, die Kacheln wachsen mit der Breite, bis eine
 *                        Spalte mehr passt. Gemeint ist immer die Breite des BILDES —
 *                        Spalten-Minimum und Kachel-Deckel rechnen das Polster und den
 *                        Rahmen der Kachel oben drauf. Die Mindestbreite hält den
 *                        Kartentext lesbar (Regel in frontend/CLAUDE.md) — in der 380px
 *                        breiten Katalog-Spalte bleibt damit eine Spalte, im Zweitfenster
 *                        in voller Breite werden es fünf. Wird eine Spalte breiter als der
 *                        Deckel, sitzt die Kachel mittig in ihrer Spalte; die Restfläche
 *                        verteilt sich also links und rechts gleich.
 *   variant='touch'    — Touch-Raster für Kacheln mit Info-Panel am Handy (Spalten-Minimum
 *                        148px, dense gap, Polster `--space-sm`): zwei Spalten ab 320px
 *                        Fensterbreite, drei ab 472px. Die Kachelbreite bestimmt 1fr,
 *                        das Minimum zählt nur die Spalten. Die 260px-Regel für lesbare
 *                        Kartenbilder gilt hier nicht — gelesen wird im `CardViewer`.
 *
 * Sub-Components:
 *   CardGrid.Full  — Zeile über die volle Rasterbreite, zwischen den Kacheln: für
 *                    Zwischenüberschriften und Trenner. Nötig, weil `browse` seine
 *                    direkten Kinder deckelt und mittig setzt — ein Trenner würde sonst
 *                    als Stummel in der Zeilenmitte stehen. Alles andere im Raster ist
 *                    eine Kachel und bleibt gedeckelt.
 *
 * Props:
 *   collapsedRows  — optional Number: kappt responsiv auf N volle Reihen. Die
 *                    Spaltenzahl misst `useGridColumns` am gerenderten Grid selbst
 *                    (kein fester Wert nötig oder erlaubt), gerendert wird dann
 *                    automatisch ein `ShowMore` für den Rest. NIE eine feste
 *                    Item-Zahl statt `collapsedRows` angeben.
 *   showMoreLabel  — optional (hiddenCount, total) => string
 */
export default function CardGrid({ variant = 'cards', className = '', collapsedRows, showMoreLabel, children }) {
  const ref = useRef(null);
  const columns = useGridColumns(ref);
  const [expanded, setExpanded] = useState(false);

  const items = Children.toArray(children);
  const collapse = collapsedRows != null && columns > 0 && !expanded;
  const visible = collapse ? items.slice(0, columns * collapsedRows) : items;
  const hidden = items.length - visible.length;

  const classes = ['card-grid', `card-grid--${variant}`];
  if (className) classes.push(className);

  return (
    <>
      <div className={classes.join(' ')} ref={ref}>{visible}</div>
      {hidden > 0 && (
        <ShowMore onClick={() => setExpanded(true)}>
          {showMoreLabel ? showMoreLabel(hidden, items.length) : `Alle ${items.length} anzeigen`}
        </ShowMore>
      )}
    </>
  );
}

CardGrid.Full = function CardGridFull({ className = '', children }) {
  return <div className={['card-grid__full', className].filter(Boolean).join(' ')}>{children}</div>;
};
